import React, { FC, useState, useEffect, useRef } from 'react';
import './../styles/LandingPage.css';
import { Icon } from '@iconify/react';
import ContactForm from './ContactForm';

// ----- Types -----
interface Service { icon: string; title: string; description: string; }
interface Testimonial { author: string; text: string; rating: number; year: number; }
interface GalleryImage { src: string; alt: string; }
type SectionId = 'home' | 'services' | 'gallery' | 'contact';

const LandingPage: FC = () => {
  // ----- State -----
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const observers = useRef<IntersectionObserver[]>([]);
  const [touchStartX, setTouchStartX] = useState(0);

  const [showForm, setShowForm] = useState(false);

  // ----- Data -----
  const sections: { id: SectionId; label: string }[] = [
    { id: 'home', label: 'Hjem' },
    { id: 'services', label: 'Tjenester' },
    { id: 'gallery', label: 'Galleri' },
    { id: 'contact', label: 'Kontakt' }
  ];

  const services: Service[] = [
    { icon: 'ic:outline-home', title: 'Innendørs maling', description: 'Vegger, tak og lister for et nytt uttrykk' },
    { icon: 'ic:outline-apartment', title: 'Utvendig maling', description: 'Slitesterke overflater for varig resultat' },
    { icon: 'ic:outline-build', title: 'Sparkling', description: 'Sparkling av nybygg og renovasjon av eldre bygg' },
    { icon: 'ic:outline-brush', title: 'Bedriftsprosjekter', description: 'Effektive løsninger for næringsbygg' },
    { icon: 'ic:outline-water', title: 'Høytrykkspyling', description: 'Grundig rengjøring og vask før maling' },
    { icon: 'ic:outline-construction', title: 'Gipsmontering', description: 'Montering og sparkling av gips' },
    { icon: 'ic:outline-build', title: 'Små Byggtjenester', description: 'Oppussing og vedlikehold' },
    { icon: 'ic:outline-build', title: 'Betongarbeid', description: 'Små betong arbeid, f.eks avretting ...' },
  ];

  const testimonials: Testimonial[] = [
    { author: 'Tomas, Stavanger', text: 'Detta har varit en toppen leverans. Veldig hyggelige folk, ikke noe søl, punktligt og raskt utført. Lykke til vidare!', rating: 5, year: 2024 },
    { author: 'Sverre, Kvernaland', text: 'Hyggelige og flinke folk. Flott utført arbeid.', rating: 5, year: 2024 },
    { author: 'Trond, Stavanger', text: 'Veldig bra utført jobb. Skal bruke KAKA ved neste anledning også 👍🏻', rating: 5, year: 2024 },
    { author: 'Julie, Sola', text: 'Haidari er flink, ekfektivt og rimelig.', rating: 5, year: 2023 },
    { author: 'John, Stavanger', text: 'Jobben ble utført helt i henhold til avtale, meget fornøyd', rating: 5, year: 2023 }
  ];

  const filteredTestimonials = testimonials.filter(t => [2025, 2024, 2023].includes(t.year));

  const nextTestimonial = () => {
    setCurrentIndex(prev => (prev === filteredTestimonials.length - 1 ? 0 : prev + 1));
  };

  const prevTestimonial = () => {
    setCurrentIndex(prev => (prev === 0 ? filteredTestimonials.length - 1 : prev - 1));
  };

  // Touch handling for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;

    if (diff > 50) nextTestimonial(); // Swipe left
    if (diff < -50) prevTestimonial(); // Swipe right
  };

  // Auto-rotate every 8 seconds
  useEffect(() => {
    const interval = setInterval(nextTestimonial, 8000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Scroll to section
  const scrollToSection = (id: SectionId) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Intersection observer for animations
  useEffect(() => {
    const elements = document.querySelectorAll('.fade-in');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    elements.forEach(el => observer.observe(el));
    observers.current.push(observer);

    return () => {
      observers.current.forEach(obs => obs.disconnect());
    };
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <div className="landing-page">
      {/* Navbar */}
      <header className="site-header">
        <nav className="navbar container">
          <div className="logo">Kaka Service AS</div>

          {/* Mobile menu button */}
          <button
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Meny"
          >
            ☰
          </button>

          <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            {sections.map(sec => (
              <li key={sec.id}>
                <button
                  onClick={() => {
                    scrollToSection(sec.id);
                    setIsMenuOpen(false);
                  }}
                >
                  {sec.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Theme toggle */}
          <button
            className="theme-toggle"
            onClick={() => setDarkMode(d => !d)}
            aria-label="Bytt tema mørk/lys"
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        </nav>
      </header>

      <main className="container">
        {/* Hero + Value Prop */}
        <section id="home" className="hero-section fade-in">
          <div className="hero-content">
            <h1 className="hero-title">Perfekte Resultater, Hver Gang</h1>
            <p className="hero-subtitle">Eksperter på maling, gips og byggtjenester for hjem og næring. Pålitelighet, kvalitet og garanti.</p>
            <div className="hero-badges">
              <span className="badge">8+ års erfaring</span>
              <span className="badge">150+ fullførte prosjekter</span>
              <span className="badge">Garantitjeneste</span>
            </div>
            <div className="hero-cta">
              <button className="cta-primary" onClick={() => scrollToSection('contact')}>Gratis befaring</button>
              <button className="cta-secondary" onClick={() => scrollToSection('services')}>Se tjenester</button>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="section process-section fade-in">
          <h2 className="section-title">Slik fungerer det</h2>
          <div className="process-steps">
            {[
              { icon: 'ic:outline-phone', title: 'Kontakt oss', desc: 'Rask respons og skreddersydd tilbud.' },
              { icon: 'ic:outline-schedule', title: 'Planlegging', desc: 'Detaljert plan og transparens.' },
              { icon: 'ic:outline-check', title: 'Utføring', desc: 'Profesjonell utførelse, på tid og budsjett.' }
            ].map((step, i) => (
              <div key={i} className="process-card">
                <Icon icon={step.icon} className="process-icon" aria-hidden />
                <h3>{`${i + 1}. ${step.title}`}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Projects */}
        <section id="gallery" className="section projects-section fade-in">
          <h2 className="section-title">Utvalgte prosjekter</h2>
          <div className="projects-grid">
            {[
              {
                id: 1,
                title: "Blokk",
                location: "Klepp Sentrum",
                img: "img/skolevegen.jpg"
              },
              {
                id: 2,
                title: "Fasademaling av enebolig i rekke",
                location: "Sandnes",
                img: "img/sandnes-bolig-i-rekke.jpg"
              },
              {
                id: 3,
                title: "Sparkling og maling",
                location: "Stavanger",
                img: "img/maling-sparkling.jpg"
              }
            ].map((project) => (
              <figure key={project.id} className="project-card">
                <div className="image-container">
                  <img 
                    src={project.img}
                    alt={`${project.title} - ${project.location}`}
                    loading="lazy"
                    className="project-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `${process.env.PUBLIC_URL}/img/placeholder.jpg`;
                    }}
                  />
                </div>
                <figcaption className="project-caption">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-location">{project.location}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Services */}
        <section id="services" className="section services-section fade-in">
          <h2 className="section-title">Våre tjenester</h2>
          <div className="services-grid">
            {services.map((s, i) => (
              <article key={i} className="service-card">
                <div className="service-icon"><Icon icon={s.icon} aria-hidden /></div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="section testimonials-section">
          <h2 className="section-title">Hva kundene sier</h2>
          <div className="total-rating">
            <Icon icon="ic:outline-star" className="testimonial-star" aria-hidden />
            <span>4.3 av 5 stjerner (45 anmeldelser på Mittanbud)</span>
          </div>

          <div
            className="testimonials-carousel"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            ref={carouselRef}
          >
            <button
              className="carousel-button prev"
              onClick={prevTestimonial}
              aria-label="Forrige testimonial"
            >
              <Icon icon="ic:outline-chevron-left" />
            </button>

            <div className="testimonials-carousel-inner">
              {filteredTestimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className={`testimonial-card ${index === currentIndex ? 'active' : ''}`}
                  style={{ transform: `translateX(${-currentIndex * 100}%)` }}
                >
                  <div className="rating-stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Icon
                        key={i}
                        icon="ic:outline-star"
                        className="testimonial-star"
                        aria-hidden
                      />
                    ))}
                  </div>
                  <blockquote className="testimonial-text">"{testimonial.text}"</blockquote>
                  <footer className="testimonial-author">— {testimonial.author}</footer>
                </div>
              ))}
            </div>

            <button
              className="carousel-button next"
              onClick={nextTestimonial}
              aria-label="Neste testimonial"
            >
              <Icon icon="ic:outline-chevron-right" />
            </button>
          </div>

          <div className="testimonial-dots">
            {filteredTestimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Vis testimonial ${index + 1}`}
              />
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="section faq-section fade-in">
          <h2 className="section-title">Ofte stilte spørsmål</h2>
          <div className="faq-grid">
            {[
              { q: 'Gir dere garanti?', a: 'Ja, alle jobber leveres med 3 måned til 12 måneders garanti avhengig av prosjekt.' },
              { q: 'Når kan dere starte?', a: 'Vi tilpasser oss ditt behov, vanligvis innen 2 uker.' },
              { q: 'Hva koster det?', a: 'Pris avhenger av prosjektets omfang – få et gratis tilbud.' }
            ].map((item, i) => (
              <details key={i} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final Contact CTA */}
        <section id="contact" className="contact-section bg-gray-50 py-16 px-4 fade-in">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Trenger du hjelp med ditt prosjekt?</h2>
        <p className="text-xl text-gray-600 mb-8">Vi står klare til å hjelpe deg</p>
        
        <div className="flex flex-col md:flex-row justify-center gap-8 mb-12">
          {/* Phone */}
          <div className="contact-method bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all">
            <div className="flex flex-col items-center">
              <Icon 
                icon="mdi:phone" 
                className="text-blue-600 text-4xl mb-4" 
              />
              <h3 className="text-xl font-semibold mb-2">Ring oss</h3>
              <a 
                href="tel:+4740390704" 
                className="text-blue-600 hover:text-blue-800 text-lg font-medium flex items-center gap-2"
              >
                <Icon icon="mdi:phone-outgoing" className="text-xl" />
                +47 40390704
              </a>
              <p className="text-gray-500 mt-2">Man-fre: 12.00 - 16.00</p>
            </div>
          </div>

          {/* Email */}
          <div className="contact-method bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all">
            <div className="flex flex-col items-center">
              <Icon 
                icon="mdi:email" 
                className="text-green-600 text-4xl mb-4" 
              />
              <h3 className="text-xl font-semibold mb-2">Send oss en e-post</h3>
              <a 
                href="mailto:post@kakaservice.no" 
                className="text-green-600 hover:text-green-800 text-lg font-medium flex items-center gap-2"
              >
                <Icon icon="mdi:email-arrow-right" className="text-xl" />
                post@kakaservice.no
              </a>
              <p className="text-gray-500 mt-2">Svar innen 48 timer</p>
            </div>
          </div>
        </div>
      </div>
    </section>
      </main>

      <footer className="main-footer fade-in">
        <div className="container">
          <small>© {new Date().getFullYear()} Kaka Service AS. Alle rettigheter reservert.</small>
          <span className="signature">Utviklet av Morteza Haidari</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
